import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import RSelect from "react-select";
import BeatLoader from "react-spinners/BeatLoader";
import { toast } from "react-toastify";
import { NacApiError, nacDownload, nacRequest, nacUpload } from "../services/nacService";
import {
  NacFormValues,
  RUOLI,
  countErrors,
  detailToForm,
  emptyIndirizzo,
  emptyNacForm,
  emptyReferente,
  formToPayload,
  clearCompanyOnly,
  hasCompanyOnlyData,
  contactRequired,
  isPrivato,
  referenteContactRequired,
  validateNacForm,
} from "../utils/nacValidation";
import { StatusBadge } from "./Nac";
import NacReadOnly from "./NacReadOnly";
import Paginator, { usePagination } from "../components/Paginator";

type Option = { value: number | string; label: string };
const toOptions = (rows: Array<{ id?: number; value: string }> = []): Option[] =>
  rows.map((r) => ({ value: r.id ?? r.value, label: String(r.value) }));

const inputCls = "w-full rounded border px-3 py-2 disabled:bg-gray-100";
const errCls = "mt-1 block text-xs text-red-600";

/** Campi dell'indirizzo di spedizione obbligatori (stessi di validateNacForm). */
const ADDRESS_REQUIRED = ["nome_destinazione", "destinatario", "indirizzo", "cap", "comune", "provincia", "nazione", "referente_consegna", "telefono_consegna"];

function Field({ label, error, children, required }: { label: string; error?: string; children: ReactNode; required?: boolean }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium">
        {label}
        {required && <span className="text-red-600"> *</span>}
      </span>
      {children}
      {error && <span className={errCls}>{error}</span>}
    </label>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-6 rounded-lg border border-neutral-200 bg-white p-5">
      <h2 className="mb-4 text-xl font-bold">{title}</h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>
    </section>
  );
}

export default function NacForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [nacId, setNacId] = useState<string | undefined>(id);
  const [values, setValues] = useState<NacFormValues>(emptyNacForm());
  const [snapshot, setSnapshot] = useState(JSON.stringify(emptyNacForm()));
  const [detail, setDetail] = useState<any>(null);
  const [lookups, setLookups] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [dup, setDup] = useState<any>(null);
  const [link, setLink] = useState<any>(null);

  const editable = !detail || detail.editable;
  const errors = useMemo(() => validateNacForm(values), [values]);
  const errorCount = countErrors(errors);
  const dirty = JSON.stringify(values) !== snapshot;
  const confirmed = !!detail?.confirmation_valid && !dirty;
  const dupErrors: string[] = dup?.errors ?? [];
  const hasDuplicates = dupErrors.length > 0;
  const dupPg = usePagination(dupErrors);
  const attachmentsPg = usePagination<any>(detail?.attachments ?? []);
  const historyPg = usePagination<any>(detail?.history ?? []);
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;

  const notify = (e: unknown, fallback: string) =>
    toast.error(e instanceof NacApiError ? e.message : fallback, { position: "top-right", className: "m-5", autoClose: 8000 });

  const applyDetail = (d: any) => {
    const form = detailToForm(d);
    setValues(form);
    setSnapshot(JSON.stringify(form));
    setDetail(d);
  };

  useEffect(() => {
    (async () => {
      try {
        setLookups(await nacRequest("/lookups"));
        if (id) {
          applyDetail(await nacRequest(`/${id}`));
          setLink(await nacRequest(`/${id}/confirmation-status`));
        }
      } catch (e) {
        notify(e, "Errore nel caricamento");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Mentre il cliente non ha risposto, controlla lo stato ogni 3 secondi (solo a scheda visibile).
  useEffect(() => {
    if (!nacId || !editable || link?.state !== "PENDING") return;
    let stopped = false;
    const tick = async () => {
      if (document.hidden) return;
      try {
        const st = await nacRequest(`/${nacId}/confirmation-status`);
        if (stopped) return;
        setLink(st);
        if (st.state !== "PENDING" && !dirtyRef.current) applyDetail(await nacRequest(`/${nacId}`));
      } catch {
        /* errore temporaneo di rete: riprova al prossimo giro */
      }
    };
    const timer = setInterval(tick, 3000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nacId, editable, link?.state]);

  const set = <K extends keyof NacFormValues>(key: K, value: NacFormValues[K]) => setValues((v) => ({ ...v, [key]: value }));
  const text = (key: keyof NacFormValues) => ({
    value: values[key] as string,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => set(key, e.target.value as any),
    className: inputCls,
  });
  const err = (key: string) => (showErrors ? errors.fields[key] : undefined);

  const saveDraft = async (): Promise<string> => {
    const payload = formToPayload(values);
    const saved = nacId
      ? await nacRequest(`/drafts/${nacId}`, "PATCH", payload)
      : await nacRequest("/drafts", "POST", payload);
    setNacId(saved.id);
    applyDetail(await nacRequest(`/${saved.id}`));
    setLink({ state: "NONE" }); // il salvataggio annulla un eventuale link in corso
    if (!nacId) navigate(`/nac/${saved.id}`, { replace: true });
    return saved.id;
  };

  const run = async (work: () => Promise<void>, fallback: string) => {
    setBusy(true);
    try {
      await work();
    } catch (e) {
      notify(e, fallback);
    } finally {
      setBusy(false);
    }
  };

  const onSaveDraft = () =>
    run(async () => {
      await saveDraft();
      toast.success("Bozza salvata", { position: "top-right", className: "m-5" });
    }, "Salvataggio non riuscito");

  const privato = isPrivato(values);
  const contactMissing = contactRequired(values);

  const changeSoggetto = (tipo: NacFormValues["tipologia_soggetto"]) => {
    if (tipo === values.tipologia_soggetto) return;
    if (tipo === "Privato") {
      if (hasCompanyOnlyData(values) && !window.confirm("Cambiando tipologia verranno cancellati i dati dei campi non applicabili. Continuare?")) return;
      setValues((v) => ({ ...clearCompanyOnly(v), tipologia_soggetto: tipo }));
    } else {
      setValues((v) => ({ ...v, tipologia_soggetto: tipo, referenti: v.referenti.length ? v.referenti : [emptyReferente(true)] }));
    }
    setDup(null);
  };

  const checkDuplicate = async () => {
    const key = privato ? values.codice_fiscale : `${values.partita_iva}${values.codice_fiscale}`;
    if (!key.trim()) return setDup(null);
    try {
      setDup(await nacRequest("/check-duplicate", "POST", { tipologia_soggetto: values.tipologia_soggetto, partita_iva: values.partita_iva, codice_fiscale: values.codice_fiscale, exclude_id: nacId }));
    } catch (e) {
      notify(e, "Controllo duplicati non riuscito");
    }
  };

  const onSendLink = () => {
    setShowErrors(true);
    if (errorCount > 0) return toast.error("Completare i campi evidenziati prima di inviare il link", { position: "top-right", className: "m-5" });
    if (!values.email.trim()) return toast.error("Inserire l'e-mail del cliente (sezione contatti) prima di inviare il link", { position: "top-right", className: "m-5" });
    run(async () => {
      const savedId = dirty || !nacId ? await saveDraft() : nacId;
      await nacRequest(`/${savedId}/confirmation-link`, "POST");
      applyDetail(await nacRequest(`/${savedId}`));
      setLink(await nacRequest(`/${savedId}/confirmation-status`));
      toast.success("Link inviato al cliente", { position: "top-right", className: "m-5" });
    }, "Invio del link non riuscito");
  };

  const onSubmit = () => {
    setShowErrors(true);
    if (errorCount > 0) return toast.error("Completare i campi evidenziati", { position: "top-right", className: "m-5" });
    run(async () => {
      let savedId = nacId;
      if (dirty || !savedId) savedId = await saveDraft(); // l'invio richiede la conferma del cliente ancora valida
      const result = await nacRequest(`/${savedId}/submit`, "POST");
      applyDetail(result);
      toast.success(`Richiesta ${result.request.nac_code} registrata`, { position: "top-right", className: "m-5" });
      navigate("/nac");
    }, "Invio non riuscito");
  };

  const onUpload = (file?: File) => {
    if (!file) return;
    run(async () => {
      const savedId = nacId ?? (await saveDraft());
      await nacUpload(savedId, file);
      applyDetail(await nacRequest(`/${savedId}`));
    }, "Caricamento non riuscito");
  };

  if (loading) return <div className="py-20 text-center"><BeatLoader color="#1d4ed8" /></div>;
  if (id && !detail) {
    return (
      <div className="mx-auto max-w-xl pb-20 pt-32 text-center">
        <p className="mb-4 text-gray-700">Richiesta NAC non trovata o non accessibile.</p>
        <Link to="/nac" className="text-sm text-blue-700 underline">← Torna all'elenco</Link>
      </div>
    );
  }

  const marche = {
    cucina: toOptions(lookups?.marca_cucina),
    elettro: toOptions(lookups?.marca_elettrodomestici),
    distr: toOptions(lookups?.distributore),
    tipologie: toOptions(lookups?.tipologia_cliente),
    categorie: (lookups?.categoria_vendita ?? []).map((c: any) => ({ value: c.value, label: c.value })) as Option[],
  };

  const multi = (key: "categoria_vendita" | "marca_cucina" | "marca_elettrodomestici" | "distributore", options: Option[], max?: number) => (
    <RSelect
      isMulti
      isDisabled={!editable}
      options={options}
      placeholder="Seleziona…"
      noOptionsMessage={() => "Nessuna opzione"}
      value={options.filter((o) => (values[key] as Array<number | string>).includes(o.value))}
      isOptionDisabled={() => max !== undefined && values[key].length >= max}
      onChange={(sel) => set(key, sel.map((o) => o.value) as any)}
    />
  );

  const tipologiaSelect = (options: Option[]) => (
    <RSelect
      isDisabled={!editable}
      options={options}
      placeholder="Seleziona…"
      noOptionsMessage={() => "Nessuna opzione"}
      value={options.find((o) => values.tipologia_cliente.includes(o.value as number)) ?? null}
      onChange={(o) => set("tipologia_cliente", o ? [o.value as number] : [])}
    />
  );

  const updateReferente =(i: number, patch: Partial<NacFormValues["referenti"][number]>) =>
    set("referenti", values.referenti.map((r, idx) => (idx === i ? { ...r, ...patch } : patch.principale ? { ...r, principale: false } : r)));
  const updateIndirizzo = (i: number, patch: Partial<NacFormValues["indirizzi_spedizione"][number]>) =>
    set("indirizzi_spedizione", values.indirizzi_spedizione.map((a, idx) => (idx === i ? { ...a, ...patch } : patch.predefinito ? { ...a, predefinito: false } : a)));

  return (
    <div className="min-h-screen bg-gray-100">
    <div className="mx-auto max-w-5xl p-4 pt-28 md:p-8 md:pt-28">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-neutral-200 bg-white p-4">
        <div className="flex items-center gap-3">
          <Link to="/nac" title="Torna all'elenco" className="material-symbols-outlined text-3xl text-blue-600" translate="no">arrow_back</Link>
          <h1 className="text-2xl font-bold uppercase">
            {detail?.request?.nac_code ?? (detail && !editable ? "Richiesta NAC" : id ? "Modifica bozza NAC" : "Nuova richiesta NAC")}
          </h1>
        </div>
        {detail && <StatusBadge status={detail.request.status} />}
      </div>

      {detail?.request?.codice_cliente && (
        <div className="mb-4 rounded border border-green-300 bg-green-50 p-3">
          Codice cliente definitivo: <b className="font-mono">{detail.request.codice_cliente}</b>. Da ora operare su questo codice.
        </div>
      )}
      {detail?.request?.email_error && !detail.request.email_sent_at && (
        <div className="mb-4 rounded border border-yellow-300 bg-yellow-50 p-3 text-sm">
          L'invio all'ufficio non è ancora riuscito. Il sistema riproverà automaticamente.
        </div>
      )}
      {hasDuplicates && (
        <div className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800" role="alert">
          <p className="mb-1 font-semibold">Non è possibile inviare la richiesta: non sono ammessi duplicati.</p>
          <ul className="list-disc pl-5">
            {dupPg.pageItems.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
          <Paginator state={dupPg} />
        </div>
      )}
      {showErrors && errorCount > 0 && (
        <div className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm">
          Ci sono {errorCount} campi da correggere o completare.
        </div>
      )}

      {!editable && (
        <NacReadOnly
          values={values}
          lookups={lookups}
          candidates={detail?.candidates ?? []}
          onPick={(codice) => {
            if (!window.confirm(`Chiudere la richiesta con il codice cliente ${codice}?`)) return;
            run(async () => {
              applyDetail(await nacRequest(`/${nacId}/pick-customer`, "POST", { codice_cliente: codice }));
              toast.success("Codice cliente collegato", { position: "top-right", className: "m-5" });
            }, "Scelta del codice cliente non riuscita");
          }}
        />
      )}

      <fieldset disabled={busy} hidden={!editable}>
        <Section title="1. Identificazione fiscale">
          <Field label="Tipologia soggetto" required>
            <select className={inputCls} value={values.tipologia_soggetto} onChange={(e) => changeSoggetto(e.target.value as NacFormValues["tipologia_soggetto"])}>
              <option value="Azienda">Azienda</option>
              <option value="Privato">Privato</option>
            </select>
          </Field>
          <Field label={privato ? "Nome e cognome" : "Ragione sociale"} required error={err("ragione_sociale")}><input {...text("ragione_sociale")} /></Field>
          {!privato && (
            <Field label="Partita IVA" required error={err("partita_iva")}>
              <input {...text("partita_iva")} inputMode="numeric" maxLength={11} onBlur={checkDuplicate} />
            </Field>
          )}
          <Field label={privato ? "Codice fiscale" : "Codice fiscale (facoltativo)"} required={privato} error={err("codice_fiscale")}>
            <input {...text("codice_fiscale")} maxLength={16} onBlur={privato ? checkDuplicate : undefined} />
          </Field>
          <Field label={privato ? "Residenza (indirizzo)" : "Indirizzo sede legale"} required error={err("indirizzo")}><input {...text("indirizzo")} /></Field>
          <Field label="Numero civico" required error={err("numero_civico")}><input {...text("numero_civico")} /></Field>
          <Field label="CAP" required error={err("cap")}><input {...text("cap")} /></Field>
          <Field label="Comune" required error={err("comune")}><input {...text("comune")} /></Field>
          <Field label="Provincia" required error={err("provincia")}><input {...text("provincia")} maxLength={2} /></Field>
          <Field label="Nazione" required error={err("nazione")}><input {...text("nazione")} /></Field>
          <Field label="PEC (facoltativa)" error={err("pec")}><input {...text("pec")} type="email" /></Field>
          <Field label="Codice SDI (facoltativo)" error={err("codice_sdi")}><input {...text("codice_sdi")} maxLength={7} /></Field>
          {!privato && <Field label="Data inizio attività (facoltativa)"><input {...text("data_inizio_attivita")} type="date" /></Field>}
        </Section>

        <Section title={privato ? "2. Contatti" : "2. Contatti aziendali"}>
          <p className="text-sm text-gray-600 md:col-span-2">
            Obbligatorio indicare almeno un recapito <span className="text-red-600">*</span> tra telefono, cellulare ed e-mail.
          </p>
          <Field label="Telefono principale" required={contactMissing} error={err("telefono")}><input {...text("telefono")} type="tel" /></Field>
          <Field label="Cellulare" required={contactMissing}><input {...text("cellulare")} type="tel" /></Field>
          <Field label="E-mail" required={contactMissing} error={err("email")}><input {...text("email")} type="email" /></Field>
          {!privato && <Field label="Sito web (facoltativo)"><input {...text("sito_web")} /></Field>}

          {!privato && <div className="md:col-span-2">
            <h3 className="mb-1 font-medium">Referenti <span className="text-red-600">*</span> {err("referenti") && <span className={errCls}>{err("referenti")}</span>}</h3>
            <p className="mb-2 text-sm text-gray-600">
              Almeno un referente (uno solo principale). Per ciascuno: nome e cognome, più almeno un telefono o un'e-mail <span className="text-red-600">*</span>.
            </p>
            {values.referenti.map((r, i) => {
              const e = (showErrors && errors.referenti[i]) || {};
              return (
                <div key={i} className="mb-3 grid grid-cols-1 gap-3 rounded border p-3 md:grid-cols-5">
                  <Field label="Nome e cognome" required error={e.nome_cognome}>
                    <input className={inputCls} value={r.nome_cognome} onChange={(ev) => updateReferente(i, { nome_cognome: ev.target.value })} />
                  </Field>
                  <Field label="Ruolo">
                    <select className={inputCls} value={r.ruolo} onChange={(ev) => updateReferente(i, { ruolo: ev.target.value })}>
                      {RUOLI.map((x) => <option key={x}>{x}</option>)}
                    </select>
                  </Field>
                  <Field label="Telefono / Cellulare" required={referenteContactRequired(r)} error={e.telefono}>
                    <input className={inputCls} value={r.telefono} onChange={(ev) => updateReferente(i, { telefono: ev.target.value })} />
                  </Field>
                  <Field label="E-mail" required={referenteContactRequired(r)} error={e.email}>
                    <input className={inputCls} value={r.email} onChange={(ev) => updateReferente(i, { email: ev.target.value })} />
                  </Field>
                  <div className="flex items-end justify-between gap-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={r.principale} onChange={() => updateReferente(i, { principale: true })} /> Principale
                    </label>
                    {values.referenti.length > 1 && (
                      <button type="button" className="text-sm text-red-700 underline" onClick={() => {
                        const rest = values.referenti.filter((_, idx) => idx !== i);
                        set("referenti", r.principale ? rest.map((x, idx) => ({ ...x, principale: idx === 0 })) : rest);
                      }}>Rimuovi</button>
                    )}
                  </div>
                </div>
              );
            })}
            <button type="button" className="text-sm text-blue-700 underline" onClick={() => set("referenti", [...values.referenti, emptyReferente()])}>
              + Aggiungi referente
            </button>
          </div>}
        </Section>

        <Section title="3. Indirizzi di spedizione">
          <div className="md:col-span-2">
            {err("indirizzi_spedizione") && <span className={errCls}>{err("indirizzi_spedizione")}</span>}
            {values.indirizzi_spedizione.map((a, i) => {
              const e = (showErrors && errors.indirizzi[i]) || {};
              const f = (k: keyof typeof a, label: string, extra?: ReactNode) => (
                <Field label={label} required={ADDRESS_REQUIRED.includes(k as string)} error={e[k as string]}>
                  <input className={inputCls} value={a[k] as string} onChange={(ev) => updateIndirizzo(i, { [k]: ev.target.value } as any)} />
                  {extra}
                </Field>
              );
              return (
                <div key={i} className="mb-3 rounded border p-3">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    {f("nome_destinazione", "Nome identificativo destinazione")}
                    {f("destinatario", privato ? "Destinatario" : "Ragione sociale / Destinatario")}
                    {f("indirizzo", "Indirizzo")}
                    {f("numero_civico", "Numero civico")}
                    {f("cap", "CAP")}
                    {f("comune", "Comune")}
                    {f("provincia", "Provincia")}
                    {f("nazione", "Nazione")}
                    {f("referente_consegna", "Referente consegna")}
                    {f("telefono_consegna", "Telefono consegna")}
                    {f("note_logistiche", "Note logistiche (facoltative)")}
                    {f("orari_ricevimento", "Orari ricevimento merce (facoltativi)")}
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={a.predefinito} onChange={() => updateIndirizzo(i, { predefinito: true })} /> Indirizzo predefinito
                    </label>
                    {values.indirizzi_spedizione.length > 1 && (
                      <button type="button" className="text-sm text-red-700 underline" onClick={() => {
                        const rest = values.indirizzi_spedizione.filter((_, idx) => idx !== i);
                        set("indirizzi_spedizione", a.predefinito ? rest.map((x, idx) => ({ ...x, predefinito: idx === 0 })) : rest);
                      }}>Rimuovi</button>
                    )}
                  </div>
                </div>
              );
            })}
            <button type="button" className="text-sm text-blue-700 underline" onClick={() => set("indirizzi_spedizione", [...values.indirizzi_spedizione, emptyIndirizzo()])}>
              + Aggiungi indirizzo di spedizione
            </button>
          </div>
        </Section>

        <Section title="4. Dati amministrativi">
          <Field label="Condizioni di pagamento (soggette a validazione aziendale)" required error={err("condizioni_pagamento")}><input {...text("condizioni_pagamento")} /></Field>
          <Field label="Banca d'appoggio" required error={err("banca_appoggio")}><input {...text("banca_appoggio")} /></Field>
          <Field label="IBAN" required error={err("iban")}><input {...text("iban")} /></Field>
          <Field label="E-mail invio documenti amministrativi" required error={err("email_documenti")}><input {...text("email_documenti")} type="email" /></Field>
          <div className="md:col-span-2">
            <Field label="Note amministrative (facoltative)"><textarea {...text("note_amministrative")} rows={2} /></Field>
          </div>
          <p className="text-xs text-gray-500 md:col-span-2">Il fido non viene richiesto: è definito esclusivamente dall'azienda.</p>
        </Section>

        <Section title="5. Dati commerciali">
          <Field label="Agente assegnato"><input className={inputCls} disabled value={detail?.request?.agent_name ?? "Assegnato automaticamente dal login"} readOnly /></Field>
          <Field label="Codice agente"><input className={inputCls} disabled value={detail?.request?.agent_code ?? "Automatico"} readOnly /></Field>
          <Field label="Categoria vendita" required error={err("categoria_vendita")}>{multi("categoria_vendita", marche.categorie)}</Field>
          <Field label="Tipologia cliente" required error={err("tipologia_cliente")}>{tipologiaSelect(marche.tipologie)}</Field>
          {!privato && (
            <>
              <Field label="Numero cucine esposte" required error={err("numero_cucine_esposte")}><input {...text("numero_cucine_esposte")} inputMode="numeric" /></Field>
              <Field label="Numero venditori" required error={err("numero_venditori")}><input {...text("numero_venditori")} inputMode="numeric" /></Field>
              <Field label="Stima cucine vendute annualmente" required error={err("stima_cucine_anno")}><input {...text("stima_cucine_anno")} inputMode="numeric" /></Field>
              <Field label="Marche cucine trattate (max 3)" error={err("marca_cucina")}>{multi("marca_cucina", marche.cucina, 3)}</Field>
              <Field label="Marche elettrodomestici trattate (max 3)" error={err("marca_elettrodomestici")}>{multi("marca_elettrodomestici", marche.elettro, 3)}</Field>
              <Field label="Distributori concorrenti (max 3)" error={err("distributore")}>{multi("distributore", marche.distr, 3)}</Field>
            </>
          )}
          <Field label="Valutazione commerciale (1–10)" required error={err("valutazione_commerciale")}><input {...text("valutazione_commerciale")} inputMode="numeric" /></Field>
          <Field label="Valutazione amministrativa (1–10)" required error={err("valutazione_amministrativa")}><input {...text("valutazione_amministrativa")} inputMode="numeric" /></Field>
          <Field label="Potenziale acquisto annuo CEDI (facoltativo)" error={err("potenziale_acquisto")}><input {...text("potenziale_acquisto")} inputMode="decimal" /></Field>
          <div className="md:col-span-2">
            <Field label="Commento" required error={err("commento")}><textarea {...text("commento")} rows={3} /></Field>
          </div>
        </Section>

      </fieldset>

      {nacId && (
        <section className="mb-6 rounded-lg border border-neutral-200 bg-white p-5">
          <h3 className="mb-2 text-xl font-bold">6. Documentazione: allegati (facoltativi, PDF/JPG/PNG, max 10 MB)</h3>
          <ul className="mb-2 text-sm">
            {attachmentsPg.pageItems.map((a: any) => (
              <li key={a.id}>
                <button type="button" className="text-blue-700 underline" onClick={() => nacDownload(nacId, a.id, a.original_name).catch((e) => notify(e, "Download non riuscito"))}>
                  {a.original_name}
                </button>{" "}
                <span className="text-gray-500">({a.size_bytes < 1024 ? `${a.size_bytes} B` : `${Math.round(a.size_bytes / 1024)} KB`})</span>
              </li>
            ))}
            {(detail?.attachments ?? []).length === 0 && <li className="text-gray-500">Nessun allegato</li>}
          </ul>
          <Paginator state={attachmentsPg} />
          {editable && <input type="file" accept=".pdf,.jpg,.jpeg,.png" disabled={busy} onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ""; }} />}
        </section>
      )}

      {editable && (
        <section className="mb-6 rounded-lg border border-neutral-200 bg-white p-5">
          <h3 className="mb-2 text-xl font-bold">Conferma del cliente</h3>
          {confirmed ? (
            <p className="text-sm text-green-700">
              Dati accettati dal cliente il {new Date(detail.request.confirmed_at).toLocaleString("it-IT")} ({detail.request.confirmation_email}). Ora è possibile inviare la richiesta.
            </p>
          ) : (
            <div className="space-y-3 text-sm">
              {link?.state === "PENDING" && !dirty && (
                <p className="rounded bg-blue-50 p-2 text-blue-800">
                  In attesa del cliente: link inviato a <b>{link.recapito}</b>, valido fino al {new Date(link.expires_at).toLocaleString("it-IT")}. Questa pagina si aggiorna da sola.
                </p>
              )}
              {link?.state === "REJECTED" && !dirty && (
                <div className="rounded border border-yellow-300 bg-yellow-50 p-2 text-yellow-900">
                  <p className="font-semibold">Il cliente ha richiesto modifiche:</p>
                  <p className="whitespace-pre-wrap">{link.customer_note}</p>
                  <p className="mt-1">Correggere i dati, salvare e inviare di nuovo il link.</p>
                </div>
              )}
              {link?.state === "EXPIRED" && !dirty && <p className="text-yellow-800">Il link è scaduto: inviarne uno nuovo.</p>}
              <p className="text-gray-700">
                Il cliente riceve per e-mail un link per controllare tutti i dati inseriti e accettarli. Indirizzo: <b>{values.email.trim() || "e-mail del cliente non inserita"}</b>
              </p>
              <button type="button" disabled={busy || hasDuplicates || !values.email.trim()} onClick={onSendLink} className="bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50">
                {link?.state === "NONE" || !link ? "Invia link al cliente" : "Invia di nuovo il link"}
              </button>
            </div>
          )}
        </section>
      )}

      {editable && (
        <div className="mb-10 flex flex-wrap gap-3">
          <button type="button" disabled={busy} onClick={onSaveDraft} className="border border-blue-600 bg-white px-5 py-2 font-semibold text-blue-600 hover:bg-blue-50 disabled:opacity-50">
            Salva bozza
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={busy || hasDuplicates || !confirmed}
            className="bg-blue-600 px-5 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Invia richiesta
          </button>
          {dirty && <span className="self-center text-sm text-yellow-700">Modifiche non salvate: salvare e inviare di nuovo il link al cliente.</span>}
        </div>
      )}

      {detail?.history?.length > 0 && (
        <section className="mb-10 rounded-lg border border-neutral-200 bg-white p-5">
          <h3 className="mb-2 text-xl font-bold">Storico pratica</h3>
          <ul className="space-y-1 text-sm">
            {historyPg.pageItems.map((h: any, i: number) => (
              <li key={i}>
                <span className="text-gray-500">{new Date(h.changed_at).toLocaleString("it-IT")}</span> – {h.from_status ?? "—"} → <b>{h.to_status}</b>
                {h.note ? <span className="text-gray-600"> ({h.note})</span> : null}
              </li>
            ))}
          </ul>
          <Paginator state={historyPg} />
        </section>
      )}
    </div>
    </div>
  );
}
